import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { useWsSubscription } from '@/hooks/useWebSocket'
import type {
  AlertSummary,
  AlertWsEvent,
  AlertsListResponse,
} from '../types'

interface UseAlertsWebSocketResult {
  /** Set of ids whose row should pulse in the next render. Auto-clears after 800ms. */
  recentlyChangedIds: Set<string>
  /** Number of new-alert events received while paused or while user is scrolled-down. */
  bufferedNewCount: number
  /** Reset the buffered counter (called when user scrolls to top / clicks the toast). */
  acknowledgeBuffered: () => void
  paused: boolean
  setPaused: (next: boolean) => void
}

interface Args {
  /** Whether the user is near the top of the queue. New alerts can fade in directly when true. */
  userIsAtTop: boolean
}

/**
 * Animation-batching configuration.
 *
 * Per the design spec: events arriving within 300 ms are flushed as a
 * single batch. When a flush contains ≥5 changes, animations are suppressed
 * entirely so a Friday-afternoon burst doesn't make the screen flicker.
 */
const BATCH_WINDOW_MS = 300
const BATCH_MAX_ANIMATED = 5

/**
 * AF-126 — Alerts WebSocket subscription.
 *
 * Buffers raw events for `BATCH_WINDOW_MS` and applies them to the React
 * Query cache as a single batch — both the page query (`alerts.list`) and
 * the infinite query (`alerts.list-infinite`) shapes are supported.
 *
 * `userIsAtTop` decides whether new alerts fade into the table or get held
 * in the "since you opened" buffer (surfaced via `bufferedNewCount`).
 */
export function useAlertsWebSocket({ userIsAtTop }: Args): UseAlertsWebSocketResult {
  const qc = useQueryClient()
  const [recentlyChangedIds, setRecentlyChangedIds] = useState<Set<string>>(() => new Set())
  const [bufferedNewCount, setBufferedNewCount] = useState(0)
  const [paused, setPaused] = useState(false)
  const pausedBufferRef = useRef<AlertSummary[]>([])

  // Animation-batch buffer.
  const batchRef = useRef<AlertWsEvent[]>([])
  const batchTimerRef = useRef<number | null>(null)

  // Track latest values via refs so the WS subscription doesn't re-bind on
  // every render and the batched flush always reads fresh state.
  const userIsAtTopRef = useRef(userIsAtTop)
  const pausedRef = useRef(paused)
  useEffect(() => {
    userIsAtTopRef.current = userIsAtTop
  })
  useEffect(() => {
    pausedRef.current = paused
  })

  const pulse = useCallback((ids: readonly string[]) => {
    if (ids.length === 0) return
    setRecentlyChangedIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) next.add(id)
      return next
    })
    window.setTimeout(() => {
      setRecentlyChangedIds((prev) => {
        const next = new Set(prev)
        for (const id of ids) next.delete(id)
        return next
      })
    }, 800)
  }, [])

  const applyToCaches = useCallback(
    (events: readonly AlertWsEvent[]) => {
      // Page-mode cache (used by tests / non-infinite consumers).
      qc.setQueriesData<AlertsListResponse | undefined>(
        { queryKey: ['alerts', 'list'] },
        (prev) => {
          if (!prev) return prev
          let next = prev
          for (const event of events) next = applyToPage(next, event)
          return next
        },
      )
      // Infinite-mode cache (the production queue page).
      qc.setQueriesData<InfiniteData<AlertsListResponse> | undefined>(
        { queryKey: ['alerts', 'list-infinite'] },
        (prev) => {
          if (!prev) return prev
          let nextPages = prev.pages
          for (const event of events) nextPages = applyToInfinitePages(nextPages, event)
          return nextPages === prev.pages ? prev : { ...prev, pages: nextPages }
        },
      )
    },
    [qc],
  )

  const flushBatch = useCallback(() => {
    batchTimerRef.current = null
    const batch = batchRef.current
    batchRef.current = []
    if (batch.length === 0) return

    // Partition events: created-while-scrolled-down (or paused) get held in
    // the "since you opened" buffer; everything else lands in the cache.
    const heldCreated: AlertSummary[] = []
    const applied: AlertWsEvent[] = []
    for (const ev of batch) {
      if (ev.type === 'alerts.created') {
        if (pausedRef.current || !userIsAtTopRef.current) {
          heldCreated.push(ev.payload)
        } else {
          applied.push(ev)
        }
      } else if (pausedRef.current) {
        // Pause = freeze the table. Drop non-create events; the analyst
        // wants stable state. They'll see updated values on un-pause via
        // a fresh refetch (queryClient invalidation could be added later).
      } else {
        applied.push(ev)
      }
    }

    if (heldCreated.length > 0) {
      pausedBufferRef.current.push(...heldCreated)
      setBufferedNewCount((c) => c + heldCreated.length)
    }

    if (applied.length > 0) {
      applyToCaches(applied)
    }

    // Animations: suppress entirely when the batch is large.
    const total = applied.length + heldCreated.length
    if (total > 0 && total < BATCH_MAX_ANIMATED) {
      const ids = new Set<string>()
      for (const ev of applied) {
        const id =
          ev.type === 'alerts.created'
            ? ev.payload.id
            : (ev.payload as { id?: string }).id
        if (id) ids.add(id)
      }
      if (ids.size > 0) pulse([...ids])
    }
  }, [applyToCaches, pulse])

  const enqueue = useCallback(
    (raw: { type: string; payload: unknown }) => {
      batchRef.current.push(raw as AlertWsEvent)
      if (batchTimerRef.current === null) {
        batchTimerRef.current = window.setTimeout(flushBatch, BATCH_WINDOW_MS)
      }
    },
    [flushBatch],
  )

  useWsSubscription(/^alerts\./, enqueue)

  const acknowledgeBuffered = useCallback(() => {
    const buffered = pausedBufferRef.current
    pausedBufferRef.current = []
    setBufferedNewCount(0)
    if (buffered.length === 0) return
    const synthetic: AlertWsEvent[] = buffered.map((p) => ({
      type: 'alerts.created',
      payload: p,
    }))
    applyToCaches(synthetic)
  }, [applyToCaches])

  useEffect(() => {
    if (!paused && pausedBufferRef.current.length > 0) {
      acknowledgeBuffered()
    }
  }, [paused, acknowledgeBuffered])

  return {
    recentlyChangedIds,
    bufferedNewCount,
    acknowledgeBuffered,
    paused,
    setPaused,
  }
}

// ---------- pure cache reducers ----------

function applyToPage(prev: AlertsListResponse, event: AlertWsEvent): AlertsListResponse {
  switch (event.type) {
    case 'alerts.created':
      return { ...prev, alerts: [event.payload, ...prev.alerts], total: prev.total + 1 }
    case 'alerts.updated':
      return {
        ...prev,
        alerts: prev.alerts.map((a) => (a.id === event.payload.id ? event.payload : a)),
      }
    case 'alerts.assigned':
      return {
        ...prev,
        alerts: prev.alerts.map((a) =>
          a.id === event.payload.id ? { ...a, assigned_to: event.payload.assigned_to } : a,
        ),
      }
    case 'alerts.status_changed':
      return {
        ...prev,
        alerts: prev.alerts.map((a) =>
          a.id === event.payload.id ? { ...a, status: event.payload.status } : a,
        ),
      }
    case 'alerts.deleted':
      return {
        ...prev,
        alerts: prev.alerts.filter((a) => a.id !== event.payload.id),
        total: Math.max(0, prev.total - 1),
      }
  }
}

function applyToInfinitePages(
  pages: AlertsListResponse[],
  event: AlertWsEvent,
): AlertsListResponse[] {
  if (pages.length === 0) return pages
  switch (event.type) {
    case 'alerts.created': {
      const [first, ...rest] = pages
      if (!first) return pages
      return [
        { ...first, alerts: [event.payload, ...first.alerts], total: first.total + 1 },
        ...rest,
      ]
    }
    case 'alerts.deleted': {
      let removed = false
      const next = pages.map((p) => {
        if (removed) return p
        const idx = p.alerts.findIndex((a) => a.id === event.payload.id)
        if (idx === -1) return p
        removed = true
        return {
          ...p,
          alerts: p.alerts.filter((_, i) => i !== idx),
          total: Math.max(0, p.total - 1),
        }
      })
      return removed ? next : pages
    }
    case 'alerts.updated':
    case 'alerts.assigned':
    case 'alerts.status_changed': {
      let touched = false
      const next = pages.map((p) => {
        const has = p.alerts.some((a) => a.id === event.payload.id)
        if (!has) return p
        touched = true
        return { ...p, alerts: p.alerts.map((a) => mergeAlert(a, event)) }
      })
      return touched ? next : pages
    }
  }
}

function mergeAlert(a: AlertSummary, event: AlertWsEvent): AlertSummary {
  switch (event.type) {
    case 'alerts.updated':
      return a.id === event.payload.id ? event.payload : a
    case 'alerts.assigned':
      return a.id === event.payload.id ? { ...a, assigned_to: event.payload.assigned_to } : a
    case 'alerts.status_changed':
      return a.id === event.payload.id ? { ...a, status: event.payload.status } : a
    default:
      return a
  }
}
